// Pick the display name based on the current language
const pickDisplayName = (arabicName, sr, language) =>
  language === "ar" ? (arabicName || sr) : (sr || arabicName);

// Generate avatar initials from the language-appropriate name
export const getAvatarInitials = (arabicName, sr, language) => {
  const displayName = pickDisplayName(arabicName, sr, language);
  return displayName
    ? displayName[0].toUpperCase() +
      (displayName.split(" ")[1]?.[0]?.toUpperCase() || "")
    : "NA";
};

// Map raw API employee → UI employee object (with ALL fields)
export const mapEmployee = (emp, language, text) => ({
  id: emp.infinityCode || emp.id,
  name: language === "ar" ? (emp.arabicName || emp.sr || "N/A") : (emp.sr || emp.arabicName || "N/A"),
  laborMode: emp.laborMode || "N/A",
  department: emp.department?.name ?? emp.employeeType ?? "N/A",
  departmentId: emp.departmentId ?? null,
  position: emp.position ?? "N/A",
  email: emp.email || "N/A",
  phone: emp.phoneNumber || "N/A",
  joinDate: emp.hiringDate,
  status: emp.employeeState || text[language].statusActive,
  company: emp.clientName || "N/A",
  level: emp.level || 0,
  hasChildren: emp.hasChildren || false,
  isExpanded: emp.isExpanded || false,
  avatar: getAvatarInitials(emp.arabicName, emp.sr, language),
  fullEmployeeData: {
    // All fields except insuranceSalary and netSalary
    clientName: emp.clientName || "N/A",
    clientCode: emp.clientCode || "N/A",
    // English name, so the search box can match it while the UI is in Arabic
    sr: emp.sr || "N/A",
    arabicName: emp.arabicName || "N/A",
    idRegion: emp.idRegion || "N/A",
    nationalId: emp.nationalId || "N/A",
    dateOfBirth: emp.dob || null,
    age: emp.age || "N/A",
    gender: emp.gender || "N/A",
    emergencyPhone: emp.emergencyPhoneNumber || "N/A",
    resignedDate: emp.resignedDate || null,
    resignReasons: emp.resignReasons || "N/A",
    beRegularDate: emp.beRegularDate || null,
    toBeRegular: emp.toBeRegular || "N/A",
    workingAgeYears: emp.workingAgeYears || 0,
    workingAgeMonths: emp.workingAgeMonths || 0,
    periodForBeingRegular: emp.periodForBeingRegular || "N/A",
    laborMode: emp.laborMode || "N/A",
    contractCompany: emp.contractCompany || "N/A",
    contractDate: emp.contractDate || null,
    termOfContract: emp.termOfContract || "N/A",
    contractExpiration: emp.contractExpiration || null,
    insuredBeginningDate: emp.insuredBeginningDate || null,
    insuranceStatus: emp.insuranceStatus || "N/A",
    monthsForInsurance: emp.monthsForInsurance || 0,
    insuranceNumber: emp.insuranceNumber || "N/A",
    // insuranceSalary: EXCLUDED
    address: emp.address || "N/A",
    arabicAddress: emp.arabicAddress || "N/A",
    city: emp.city || "N/A",
    country: emp.country || "N/A",
    religion: emp.religion || "N/A",
    maritalStatus: emp.maritalStatus || "N/A",
    nationality: emp.nationality || "N/A",
    graduateInstitution: emp.graduateInstitution || "N/A",
    major: emp.major || "N/A",
    graduationYear: emp.graduationYear || "N/A",
    idCopy: emp.idCopy ?? false,
    gradCertificateOriginal: emp.gradCertificateOriginal ?? false,
    birthCertificateCopy: emp.birthCertificateCopy ?? false,
    militaryServiceCertificate: emp.militaryServiceCertificate ?? false,
    laborLetterOriginal: emp.laborLetterOriginal ?? false,
    noCrimeCertificate: emp.noCrimeCertificate ?? false,
    photosCount: emp.photosCount ?? false,
    uniformSize: emp.uniformSize || "N/A",
    formNo6Original: emp.formNo6Original ?? false,
    medicalCheckForm111: emp.medicalCheckForm111 ?? false,
    didMedicalCheck: emp.didMedicalCheck || false,
    medicalReportOriginal: emp.medicalReportOriginal || false,
    lawyerCertificateCopy: emp.lawyerCertificateCopy || false,
    driversLicenseCopy: emp.driversLicenseCopy || false,
    testEngineerCertificate: emp.testEngineerCertificate || false,
    accountantCertificate: emp.accountantCertificate || false,
    militaryStatus: emp.militaryStatus || "N/A",
    healthCertificateOB: emp.healthCertificateOB || false,
    healthCertExpiryDate: emp.healthCertExpiryDate || null,
    healthCertStillValid: emp.healthCertStillValid || false,
    drivingLicenseDegree: emp.drivingLicenseDegree || "N/A",
    drivingLicenseExpiry: emp.drivingLicenseExpiry || null,
    drivingLicenseStillValid: emp.drivingLicenseStillValid || false,
    lastContractDate: emp.lastContractDate || null,
    lastContractTerm: emp.lastContractTerm || "N/A",
    lastContractExpiration: emp.lastContractExpiration || null,
    medicalInsuranceStage: emp.medicalInsuranceStage || "N/A",
    delivered: emp.delivered || false,
    medicalNumber: emp.medicalNumber || "N/A",
    bankName: emp.bankName || "N/A",
    // netSalary: EXCLUDED
    transportationAllowances: emp.transportationAllowances || 0,
    variableSalary: emp.variableSalary || 0,
    workingCompany: emp.workingCompany || "N/A",
    createdAt: emp.created_at || null,
    updatedAt: emp.updated_at || null,
    firstLineManagerName: emp.firstLineManagerName || "N/A",
    secondLineManagerName: emp.secondLineManagerName || "N/A",
    firstLineManagerInfinityCode: emp.firstLineManagerInfinityCode || null,
    secondLineManagerInfinityCode: emp.secondLineManagerInfinityCode || null,
  },
});

// Map raw API archived employee → UI archived employee object
export const mapArchivedEmployee = (emp, language) => ({
  id: emp.infinityCode || emp.id,
  name: language === "ar" ? (emp.arabicName || emp.sr || "N/A") : (emp.sr || emp.arabicName || "N/A"),
  laborMode: emp.laborMode || "N/A",
  department: emp.department?.name ?? emp.employeeType ?? "N/A",
  departmentId: emp.departmentId ?? null,
  position: emp.position ?? "N/A",
  email: emp.email || "N/A",
  phone: emp.phoneNumber || "N/A",
  salary: emp.netSalary || 0,
  joinDate: emp.hiringDate,
  status: "Archived", // All archived employees have status "Archived"
  company: emp.clientName || "N/A",
  archivedDate: emp.archivedDate || new Date().toISOString(), // Archive date
  // Generate avatar initials
  avatar: getAvatarInitials(emp.arabicName, emp.sr, language),
});
